import { useEffect, useRef, useState } from 'preact/hooks';
import {
  clearPromoCode,
  getPromoRecord,
  setPromoRecord,
  type PromoApplyMode,
  type PromoScope,
  type PromoStorageRecord,
} from './promoStorage';
import { oNumberMatches, parsePromoParams } from './promoParams';
import { onPromoRejected } from './promoEvents';

export interface PromoEligibility {
  productIds: string[];
  categoryIds: string[];
  manufacturerIds: string[];
  tagIds: string[];
  productStreamIds: string[];
  unknown: boolean;
}

export interface PromoMetadata {
  code: string;
  status: 'active' | 'inactive' | 'expired' | 'scheduled' | 'notfound' | 'service_unavailable';
  name: string | null;
  discountType: 'percentage' | 'absolute' | 'fixed' | 'fixed_unit' | null;
  discountValue: number | null;
  discountScope: 'cart' | 'delivery' | 'set' | 'set_group' | 'pickitem' | string | null;
  validUntil: string | null;
  minOrderValue: number | null;
  eligibility: PromoEligibility;
  // Shopware-maintained rich text (ORT-3110). When present, it replaces the composed
  // headline in the badge. Only set for promotions sourced from /pdp-data; Stage-1
  // Pimcore metadata leaves it undefined and keeps the composed text.
  userDescription?: string | null;
  // Whether the discount is already reflected on the PDP as a Streichpreis (ORT-3110);
  // switches the applied subline to "Rabatt wird automatisch abgezogen".
  priceOnPdp?: boolean;
}

export interface PromoProductContext {
  id: string;
  // oNumber of the current variant, used for ?pron= o-number scoping (ORT-3077 A).
  oNumber?: string | null;
  categoryIds?: string[] | null;
  manufacturerId?: string | null;
  tagIds?: string[] | null;
  productStreamIds?: string[] | null;
  // Anchor identifiers used to gate banner visibility against the stored scope.
  // Each is the value that would match a record with the corresponding scope.
  productAnchor?: string | null;     // OrthomolCorporateProduct id (matches scope=p)
  dosageFormAnchor?: string | null;  // OrthomolCorporateProductDosageForm id (matches scope=d)
  packsizeAnchor?: string | null;    // Shopware product id == uniquePacksizeId (matches scope=s)
}

export type PromoState =
  | { kind: 'idle' }
  | { kind: 'loading'; code: string }
  | { kind: 'applied'; code: string; metadata: PromoMetadata; eligibleForThisProduct: boolean | null }
  | { kind: 'invalid'; code: string; metadata: PromoMetadata }
  | { kind: 'unavailable'; code: string }
  | { kind: 'rejected'; code: string }
  | { kind: 'error'; code: string };

interface UrlPromo {
  code: string;
  scope: PromoScope;
  applyMode: PromoApplyMode;
  oNumbers: string[] | null;
  suppressOffers: boolean;
}

function readPromoFromUrl(): UrlPromo | null {
  if (typeof window === 'undefined') return null;
  const parsed = parsePromoParams(window.location.search);
  if (!parsed) return null;
  return {
    code: parsed.code,
    scope: parsed.scope,
    applyMode: parsed.applyMode,
    oNumbers: parsed.oNumbers,
    suppressOffers: parsed.suppressOffers,
  };
}

function isInvalidStatus(status: PromoMetadata['status']): boolean {
  return status === 'notfound' || status === 'expired' || status === 'inactive' || status === 'scheduled';
}

/**
 * For a record with a specific scope+anchor, decide whether the current PDP is
 * still within that scope. Returns:
 *   true  → render the banner
 *   false → out of scope, hide the banner (record stays in storage)
 *   null  → can't decide yet (no anchor / no product context); stay visible
 */
function isWithinScope(record: PromoStorageRecord, product: PromoProductContext | null): boolean | null {
  if (!record.anchor) return null;
  if (!product) return null;
  const currentAnchor =
    record.scope === 's' ? product.packsizeAnchor :
    record.scope === 'd' ? product.dosageFormAnchor :
    product.productAnchor;
  if (currentAnchor == null) return null;
  return currentAnchor === record.anchor;
}

function anchorForScope(scope: PromoScope, product: PromoProductContext | null): string | null {
  if (!product) return null;
  if (scope === 's') return product.packsizeAnchor ?? null;
  if (scope === 'd') return product.dosageFormAnchor ?? null;
  return product.productAnchor ?? null;
}

/**
 * Captures a `?promo=…` URL parameter on PDP mount, looks up its metadata
 * via Pimcore, and (if valid) registers it as a promotion line-item on the
 * user's Shopware cart so it auto-applies once a matching product is added.
 *
 * The captured record also remembers the Newsletter-intended scope (?prsc=)
 * and apply mode (?pra=). Subsequent PDPs read the record and gate the banner
 * by scope; the cart layer reads the apply mode to decide whether existing
 * manual codes should be removed on add-to-cart.
 */
export function usePromoCode(product?: PromoProductContext | null): PromoState {
  const [state, setState] = useState<PromoState>({ kind: 'idle' });
  // Capture is a one-shot per page load. We re-enter the effect when product
  // anchors hydrate from the embedded JSON (so we can record a non-null
  // anchor), but the ref guards against re-POST on subsequent variant switches.
  const capturedRef = useRef(false);
  // Cached scope/anchor + the banner to restore, so the eligibility effect below
  // can re-gate against a new product context without re-fetching from Pimcore.
  const cacheRef = useRef<{
    code: string;
    scope: PromoScope;
    anchor: string | null;
    // o-number scoping from ?pron= (ORT-3077 A); used by the re-gate effect on variant switch.
    oNumbers: string[] | null;
    // `unavailable` carries no metadata (it only needs the code), which lets the
    // eligibility effect scope-gate it the same way it gates the applied banner.
    banner:
      | { kind: 'applied'; metadata: PromoMetadata }
      | { kind: 'unavailable' };
  } | null>(null);
  // Flipped true only on genuine unmount, so an in-flight metadata fetch never
  // calls setState on a gone component. We intentionally do NOT cancel on
  // dependency re-runs: the fetch is one-shot (capturedRef), so a variant switch
  // mid-fetch must not abandon it and strand the hook in 'loading' forever.
  const unmountedRef = useRef(false);
  useEffect(() => () => { unmountedRef.current = true; }, []);
  // Always-current product snapshot. The capture effect's async closes over the
  // product as it was when the fetch *started* — often the preloaded, partial
  // snapshot lacking Store API fields like categoryIds. Reading the ref instead
  // lets the initial eligibility reflect an enriched product that arrived while
  // the metadata request was still in flight.
  const productRef = useRef(product);
  productRef.current = product;

  useEffect(() => {
    if (capturedRef.current) return;

    const fromUrl = readPromoFromUrl();
    const fromStorage = getPromoRecord();

    // URL takes precedence — a fresh Newsletter-click overrides whatever was
    // remembered before. Otherwise we resume from the last validated code.
    // Out-of-scope stored codes are *not* surfaced as a banner but stay in
    // storage so the user can return to the originally-linked PDP.
    if (!fromUrl) {
      if (!fromStorage) return;
      const inScope = isWithinScope(fromStorage, product ?? null);
      if (inScope === false) {
        capturedRef.current = true; // decision made; don't re-evaluate
        return;
      }
      // inScope === null means we still don't know — wait for anchors to hydrate.
      if (inScope === null && product == null) return;
    } else if (product == null) {
      // Fresh URL capture but anchors not yet available — wait one tick so
      // the stored record can pin to the right anchor for prsc=p/d/s gating
      // on subsequent PDPs. Without this, the anchor would be null and the
      // banner would leak to unrelated products.
      return;
    }

    capturedRef.current = true;
    const code = fromUrl?.code ?? fromStorage!.code;
    const scope: PromoScope = fromUrl?.scope ?? fromStorage!.scope;
    const applyMode: PromoApplyMode = fromUrl?.applyMode ?? fromStorage!.applyMode;
    const oNumbers: string[] | null = fromUrl?.oNumbers ?? fromStorage?.oNumbers ?? null;
    const suppressOffers: boolean = fromUrl?.suppressOffers ?? fromStorage?.suppressOffers ?? false;

    setState({ kind: 'loading', code });

    (async () => {
      let metadata: PromoMetadata;
      try {
        const res = await fetch(`/shop/promo/${encodeURIComponent(code)}/metadata`, {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`metadata ${res.status}`);
        metadata = (await res.json()) as PromoMetadata;
      } catch {
        if (unmountedRef.current) return;
        // If we have a previously-validated record for this exact code, keep
        // it in storage and show the unavailable banner — Shopware is the
        // final arbiter at cart-add time anyway. Only when the offending code
        // came purely from the URL (no storage record) do we go to 'error'
        // and render nothing, because we have no confirmation it's a real code.
        if (fromStorage && fromStorage.code === code) {
          // A pron-scoped code not covering this product stays "for other products" even when the
          // metadata endpoint is unreachable — never present it as pending here (ORT-3077). Use the
          // MERGED oNumbers (URL ?pron= takes precedence over the stored record), so a fresh
          // ?promo=&pron= link is honored even when the stored record has no oNumbers.
          if (oNumberIneligible(oNumbers, productRef.current ?? null)) {
            const metadata = otherProductsMetadata(code);
            cacheRef.current = {
              code,
              scope: fromStorage.scope,
              anchor: fromStorage.anchor,
              oNumbers,
              banner: { kind: 'applied', metadata },
            };
            setState({ kind: 'applied', code, metadata, eligibleForThisProduct: false });
            return;
          }
          // Same code we previously validated — Shopware is the arbiter at
          // cart-add anyway, so keep it and show the unavailable banner. Cache
          // scope/anchor so the eligibility effect scope-gates it on variant
          // switch (otherwise it would leak to every variant).
          cacheRef.current = {
            code,
            scope: fromStorage.scope,
            anchor: fromStorage.anchor,
            oNumbers,
            banner: { kind: 'unavailable' },
          };
          setState({ kind: 'unavailable', code });
        } else {
          // A fresh URL code we couldn't confirm, with no matching stored record.
          // Drop any *different* stale code so checkout can't apply a promo the
          // banner never reflected.
          clearPromoCode();
          setState({ kind: 'error', code });
        }
        return;
      }

      if (unmountedRef.current) return;

      // Anchor for the Newsletter-intended scope (prsc); shared by the
      // service_unavailable and active branches below.
      //
      // When the newsletter scoped by explicit o-numbers (?pron=, ORT-3077 A), eligibility is
      // decided per product by o-number membership (resolveEligibility), NOT by pinning the record
      // to whichever PDP happens to be opened first. Keep the anchor null in that case, otherwise
      // visiting a non-matching PDP first would pin the record to that product and isWithinScope
      // would then hide the banner on the intended product (customer feedback ORT-3077, 212490).
      const usesONumberScope = !!oNumbers && oNumbers.length > 0;
      const anchor = usesONumberScope ? null : anchorForScope(scope, product ?? null);

      if (isInvalidStatus(metadata.status)) {
        // The captured code is invalid, so drop whatever is in storage — incl. a
        // *different* older code. Leaving it would let checkout silently apply a
        // promo the banner never confirmed (the banner shows this code invalid).
        clearPromoCode();
        setState({ kind: 'invalid', code, metadata });
        return;
      }

      // ORT-3077: a code whose ?pron= o-number list does not include this product is definitively
      // "for other products" regardless of whether Shopware could confirm it right now. Show the
      // informational "andere Produkte" banner for every non-invalid status (active AND
      // service_unavailable), never applied/pending. Without this, a service_unavailable lookup
      // rendered "Code vorgemerkt … wird beim Bestellabschluss geprüft" on a non-matching product
      // (customer feedback, ORT-3077 staging follow-up on comment 212490).
      if (oNumberIneligible(oNumbers, productRef.current ?? null)) {
        setPromoRecord({ code, scope, anchor, applyMode, oNumbers, suppressOffers });
        cacheRef.current = { code, scope, anchor, oNumbers, banner: { kind: 'applied', metadata } };
        setState({ kind: 'applied', code, metadata, eligibleForThisProduct: false });
        return;
      }

      if (metadata.status === 'service_unavailable') {
        // Store the code optimistically so the banner promise ("wird beim
        // Bestellabschluss geprüft") can actually be fulfilled at cart-add
        // time. Shopware is the source of truth; if it rejects the code at
        // add-to-cart, the existing droppedPromotionCodes path clears it and
        // fires notifyPromoRejected. The short server-side TTL_UNAVAILABLE
        // (30s) keeps stale verdicts from sticking once Shopware recovers.
        setPromoRecord({ code, scope, anchor, applyMode, oNumbers, suppressOffers });
        // Cache scope/anchor (banner 'unavailable') so the eligibility effect
        // scope-gates it just like the 'applied' banner.
        cacheRef.current = { code, scope, anchor, oNumbers, banner: { kind: 'unavailable' } };
        setState({ kind: 'unavailable', code });
        return;
      }

      // Active. Remember the code so the next Add-to-Cart can apply it together
      // with the product. We deliberately do NOT POST to the cart here — an
      // empty cart would silently drop the promotion line item immediately.
      setPromoRecord({ code, scope, anchor, applyMode, oNumbers, suppressOffers });
      cacheRef.current = { code, scope, anchor, oNumbers, banner: { kind: 'applied', metadata } };

      // Use the latest product (not the start-of-fetch closure): Store API
      // fields like categoryIds may have hydrated while metadata was in flight.
      const eligibleForThisProduct = resolveEligibility(metadata, productRef.current ?? null, oNumbers);
      setState({ kind: 'applied', code, metadata, eligibleForThisProduct });
    })();

    // No cleanup that cancels the fetch — it is one-shot (capturedRef) and a
    // variant switch must not abandon it (that would strand us in 'loading').
    // unmountedRef alone guards against setState after unmount.
    // Re-run as the product anchors hydrate so the capture pins to the right
    // anchor. capturedRef ensures we only POST the metadata lookup once per
    // page load, even across variant switches. The async reads productRef for
    // the latest product, so it does not need the Store API fields as deps.
  }, [product?.productAnchor, product?.dosageFormAnchor, product?.packsizeAnchor]);

  // Recompute per-product eligibility + scope on variant switch.
  //
  // The capture effect above guards against re-fetching metadata, which means
  // a dosage-form / packsize switch on the same PDP would otherwise leave the
  // banner showing stale eligibility ("applies to this product" still true
  // after the user navigated to a non-eligible variant). This effect fixes
  // that by re-deriving the visible state from the cached metadata each time
  // an anchor changes, without touching the network.
  useEffect(() => {
    const cache = cacheRef.current;
    if (!cache) return;

    // Scope re-check: if the user moved out of the stored scope (e.g. prsc=s
    // anchored to 30er, user now on 60er), hide the banner without clearing
    // storage — returning to the original variant should bring it back. This
    // applies to both the 'applied' and the 'unavailable' banner.
    if (cache.anchor && product) {
      const currentAnchor =
        cache.scope === 's' ? product.packsizeAnchor :
        cache.scope === 'd' ? product.dosageFormAnchor :
        product.productAnchor;
      if (currentAnchor != null && currentAnchor !== cache.anchor) {
        setState({ kind: 'idle' });
        return;
      }
    }

    // In scope: restore/refresh the cached banner. Don't clobber a post-add
    // 'rejected' verdict (or any other kind the cache doesn't own).
    if (cache.banner.kind === 'unavailable') {
      // ORT-3077: if the o-number scope now excludes this product (e.g. the capture cached the
      // 'unavailable' banner before the o-number had hydrated), show "andere Produkte" instead of a
      // pending banner. Keeps a pron-scoped code from ever reading as applicable on a non-match.
      if (oNumberIneligible(cache.oNumbers, product ?? null)) {
        const metadata = otherProductsMetadata(cache.code);
        setState((prev) =>
          prev.kind === 'idle' || prev.kind === 'unavailable' || prev.kind === 'applied'
            ? { kind: 'applied', code: cache.code, metadata, eligibleForThisProduct: false }
            : prev
        );
        return;
      }
      // Back in scope: restore the pending 'unavailable' banner. Include 'applied' so a variant
      // switch from a non-matching to a matching variant clears the "andere Produkte" applied state
      // this effect itself set above (otherwise it would stick). 'rejected' etc. are left untouched.
      setState((prev) =>
        prev.kind === 'idle' || prev.kind === 'unavailable' || prev.kind === 'applied'
          ? { kind: 'unavailable', code: cache.code }
          : prev
      );
      return;
    }

    const metadata = cache.banner.metadata;
    setState((prev) => {
      // Only refresh the per-product fields of the 'applied' state (or restore
      // it from 'idle' on returning into scope). Other kinds (loading / invalid
      // / rejected) are unaffected by product context.
      if (prev.kind !== 'applied' && prev.kind !== 'idle') return prev;
      const eligibleForThisProduct = resolveEligibility(metadata, product ?? null, cache.oNumbers);
      return { kind: 'applied', code: cache.code, metadata, eligibleForThisProduct };
    });
    // Also depend on the eligibility-relevant Store API fields (not just the
    // prsc anchors): they hydrate in a second phase (preloaded → Store API)
    // without the product id / anchors changing, so without these the banner
    // would keep a stale "other products" verdict after enrichment. Arrays are
    // joined to a stable primitive so identity churn doesn't thrash the effect.
  }, [
    product?.productAnchor,
    product?.dosageFormAnchor,
    product?.packsizeAnchor,
    product?.id,
    product?.oNumber,
    product?.categoryIds?.join(','),
    product?.manufacturerId,
    product?.tagIds?.join(','),
    product?.productStreamIds?.join(','),
  ]);

  // React when the Cart layer reports that Shopware rejected our code at
  // add-to-cart time. Updater form so we always see the latest state, not a
  // stale closure captured at subscription time.
  useEffect(() => {
    return onPromoRejected((rejectedCode) => {
      // Drop the cached banner for a rejected code so the eligibility effect
      // can't resurrect the 'applied'/'unavailable' banner when the shopper
      // leaves a scoped variant and returns (storage is already cleared, so the
      // code would never re-attach at cart-add — the banner must not imply it).
      if (cacheRef.current?.code === rejectedCode) {
        cacheRef.current = null;
      }
      setState((prev) => {
        if ((prev.kind === 'applied' || prev.kind === 'unavailable') && prev.code === rejectedCode) {
          return { kind: 'rejected', code: rejectedCode };
        }
        return prev;
      });
    });
  }, []);

  return state;
}

/**
 * Decide whether the current PDP product is eligible for the promo.
 *
 * - `true`  → at least one matcher in the promo's scope hits the current product
 * - `false` → promo has matchers but none of them include the current product
 * - `null`  → cart-wide promo, or we can't reliably decide (unknown conditions,
 *             missing product context). Banner falls back to generic messaging.
 */
/**
 * Resolve per-product eligibility. When the newsletter passed an explicit o-number list
 * (?pron=, ORT-3077 A) we scope by o-number membership: this is the authoritative signal when
 * the link pointed to a non-PDP (there was no product to infer scope from). Falls back to the
 * Pimcore-metadata matching when no o-number list was given. Entries may be wildcard patterns
 * (ORT-3152), so membership goes through oNumberMatches rather than a plain includes().
 */
function resolveEligibility(
  metadata: PromoMetadata,
  product: PromoProductContext | null,
  oNumbers: string[] | null
): boolean | null {
  if (oNumbers && oNumbers.length > 0) {
    if (!product?.oNumber) return null; // product not hydrated yet, can't decide
    return oNumberMatches(oNumbers, product.oNumber);
  }
  return computeEligibility(metadata.eligibility, product);
}

/**
 * True only when the code is scoped by an explicit o-number list (?pron=, ORT-3077 A), the current
 * product's o-number is known, and it is NOT in that list. This is a definitive, client-side
 * "for other products" signal that does not depend on Shopware confirming the code, so it must gate
 * the banner in every metadata status (active, service_unavailable, endpoint unreachable) and never
 * let a non-matching product show the code as applied/pending.
 */
function oNumberIneligible(oNumbers: string[] | null, product: PromoProductContext | null): boolean {
  if (!oNumbers || oNumbers.length === 0) return false;
  if (!product?.oNumber) return false;
  return !oNumberMatches(oNumbers, product.oNumber);
}

// Minimal metadata for the "andere Produkte" banner when we could not obtain real metadata (endpoint
// unreachable) but pron already tells us the code is for other products.
function otherProductsMetadata(code: string): PromoMetadata {
  return {
    code,
    status: 'service_unavailable',
    name: null,
    discountType: null,
    discountValue: null,
    discountScope: null,
    validUntil: null,
    minOrderValue: null,
    eligibility: { productIds: [], categoryIds: [], manufacturerIds: [], tagIds: [], productStreamIds: [], unknown: false },
  };
}

function computeEligibility(eligibility: PromoEligibility, product: PromoProductContext | null): boolean | null {
  if (eligibility.unknown) return null;

  const hasMatchers =
    eligibility.productIds.length > 0 ||
    eligibility.categoryIds.length > 0 ||
    eligibility.manufacturerIds.length > 0 ||
    eligibility.tagIds.length > 0 ||
    eligibility.productStreamIds.length > 0;

  if (!hasMatchers) return null;        // cart-wide
  if (!product) return null;             // can't decide without product context

  const inAny = (haystack: string[], needles: Array<string | undefined | null>) =>
    needles.some((n) => n != null && haystack.includes(n));

  // A direct product-id match is always decisive.
  if (eligibility.productIds.includes(product.id)) return true;

  // Scope matchers (category/manufacturer/tag/stream) need the product's scope ids. The
  // consolidated /pdp-data product does not carry them (promotions are scoped server-side
  // instead), so when the promo relies on scope matchers but we have no scope data we cannot
  // claim "other products" — degrade to the generic banner (null) rather than a wrong false.
  // Eligibility straight from /pdp-data promotions is handled separately (ORT-3060/3089).
  const hasScopeMatchers =
    eligibility.categoryIds.length > 0 ||
    eligibility.manufacturerIds.length > 0 ||
    eligibility.tagIds.length > 0 ||
    eligibility.productStreamIds.length > 0;
  const productHasScopeData =
    (product.categoryIds?.length ?? 0) > 0 ||
    product.manufacturerId != null ||
    (product.tagIds?.length ?? 0) > 0 ||
    (product.productStreamIds?.length ?? 0) > 0;
  if (hasScopeMatchers && !productHasScopeData) return null;

  return (
    inAny(eligibility.categoryIds, product.categoryIds ?? []) ||
    inAny(eligibility.manufacturerIds, [product.manufacturerId]) ||
    inAny(eligibility.tagIds, product.tagIds ?? []) ||
    inAny(eligibility.productStreamIds, product.productStreamIds ?? [])
  );
}
